import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Instagram, Youtube } from "lucide-react";
import { MASCOT_URL, SOCIAL_LINKS, FOURTHWALL_URL, TikTokIcon, XIcon, PawIcon } from "@/lib/brand";

const MENU_MAIN = [
    { to: "/", label: "Home", thumb: "/menu/panel-2.png" },
    { to: "/about", label: "About", thumb: "/menu/panel-1.png" },
    { to: "/my-5", label: "MY 5", thumb: "/menu/panel-7.png" },
    { to: FOURTHWALL_URL, label: "Shop", thumb: "/menu/panel-9.png" },
    { to: "/pan-school", label: "PAN School / FAQ", thumb: "/menu/panel-5.png" },
    { to: "/get-involved", label: "Get Involved", thumb: "/menu/panel-6.png" },
    { to: "#connect", label: "Social / Connect", thumb: "/menu/panel-3.png" },
    { to: "#radio", label: "PURP ALERT RADIO", thumb: "/menu/panel-4.png" },
];

const MENU_MORE = [
    { to: "/#explore", label: "Explore the Network" },
    { to: "/discover", label: "Join the Network" },
    { to: "/create-build", label: "Create / Build" },
    { to: "/community", label: "Community" },
    { to: "/media", label: "Media" },
    { to: "/gallery", label: "Gallery" },
];

const SOCIAL_ICONS = { instagram: Instagram, youtube: Youtube, tiktok: TikTokIcon, x: XIcon };

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const goAnchor = (e, hash) => {
        e.preventDefault();
        setOpen(false);
        const id = hash.replace("/#", "").replace("#", "");
        const scroll = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        if (location.pathname !== "/") {
            navigate("/");
            setTimeout(scroll, 450);
        } else {
            scroll();
        }
    };

    const renderItem = (l, i) => {
        const testid = `menu-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
        const cls =
            "group flex items-center justify-between gap-4 w-full py-3.5 border-b border-white/10 font-display font-extrabold uppercase text-2xl sm:text-3xl text-slate-200 hover:text-purp-neon transition-colors duration-300";
        const inner = (
            <>
                <span className="flex items-center gap-4 min-w-0">
                    <img
                        src={l.thumb}
                        alt=""
                        loading="lazy"
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl object-cover border border-purp/30 opacity-80 shrink-0 transition-all duration-300 group-hover:opacity-100 group-hover:scale-105 group-hover:border-purp-neon/70 group-hover:shadow-[0_0_20px_rgba(168,85,247,0.55)]"
                    />
                    <span className="truncate">{l.label}</span>
                </span>
                <PawIcon className="w-5 h-5 text-purp/40 group-hover:text-purp transition-colors duration-300 shrink-0" />
            </>
        );
        if (l.to.startsWith("http")) {
            return (
                <a key={l.label} href={l.to} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className={cls} data-testid={testid}>
                    {inner}
                </a>
            );
        }
        if (l.to.startsWith("#") || l.to.startsWith("/#")) {
            return (
                <button key={l.label} onClick={(e) => goAnchor(e, l.to)} className={`${cls} text-left`} data-testid={testid}>
                    {inner}
                </button>
            );
        }
        return (
            <Link key={l.to} to={l.to} className={cls} data-testid={testid}>
                {inner}
            </Link>
        );
    };

    return (
        <>
            <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-obsidian/75 backdrop-blur-xl" data-testid="site-navbar">
                <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link">
                        <img
                            src={MASCOT_URL}
                            alt="PURP ALERT #5 — the official mascot"
                            className="w-9 h-9 md:w-10 md:h-10 rounded-xl object-cover border border-purp/40 card-glow transition-transform duration-300 group-hover:rotate-3"
                        />
                        <span className="leading-none">
                            <span className="block font-display italic font-extrabold text-purp text-lg md:text-xl tracking-wide">
                                PURP ALERT
                            </span>
                            <span className="block font-mono text-[9px] md:text-[10px] tracking-[0.45em] text-slate-300">
                                NETWORK
                            </span>
                        </span>
                    </Link>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/discover"
                            data-testid="nav-join-cta"
                            className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-purp px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-obsidian transition-shadow duration-300 hover:shadow-[0_0_32px_rgba(168,85,247,0.6)]"
                        >
                            Join the Network <ArrowUpRight className="w-3.5 h-3.5" />
                        </Link>
                        <button
                            onClick={() => setOpen(true)}
                            data-testid="nav-menu-toggle"
                            aria-label="Open menu"
                            className="inline-flex items-center gap-2 rounded-full border border-purp/50 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] font-bold text-purp-neon hover:bg-purp/10 transition-colors duration-300"
                        >
                            <Menu className="w-4 h-4" /> Menu
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <div className="fixed inset-0 z-[80]" data-testid="side-menu">
                        <motion.div
                            className="absolute inset-0 bg-obsidian/80 backdrop-blur-sm"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                            data-testid="side-menu-backdrop"
                        />
                        <motion.aside
                            className="absolute right-0 top-0 h-full w-full max-w-md bg-panel border-l border-purp/30 flex flex-col"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", stiffness: 300, damping: 32 }}
                        >
                            <div className="flex items-center justify-between px-6 h-16 md:h-20 border-b border-white/10 shrink-0">
                                <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-purp-neon flex items-center gap-2">
                                    <PawIcon className="w-4 h-4" /> Navigate
                                </span>
                                <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="side-menu-close" className="text-slate-300 hover:text-purp-neon transition-colors p-2">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>
                            <nav className="flex-1 overflow-y-auto px-6 py-4" data-testid="side-menu-main">
                                {MENU_MAIN.map((l, i) => renderItem(l, i))}
                                <p className="mt-8 mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">More of the Network</p>
                                {MENU_MORE.map((l, i) => {
                                    const testid = `menu-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
                                    const cls = "group flex items-center gap-2.5 py-3 font-mono text-xs uppercase tracking-[0.22em] text-slate-400 hover:text-purp-neon transition-colors duration-300";
                                    const moreInner = (
                                        <>
                                            <PawIcon className="w-3.5 h-3.5 text-purp/40 group-hover:text-purp transition-colors duration-300 shrink-0" />
                                            <span>{l.label}</span>
                                        </>
                                    );
                                    if (l.to.startsWith("/#")) {
                                        return (
                                            <button key={l.label} onClick={(e) => goAnchor(e, l.to)} className={`${cls} text-left w-full`} data-testid={testid}>
                                                {moreInner}
                                            </button>
                                        );
                                    }
                                    return (
                                        <Link key={l.to} to={l.to} className={cls} data-testid={testid}>
                                            {moreInner}
                                        </Link>
                                    );
                                })}
                            </nav>
                            <div className="shrink-0 border-t border-white/10 px-6 py-5">
                                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Connect with PAN</p>
                                <div className="flex gap-2.5" data-testid="menu-socials">
                                    {SOCIAL_LINKS.map((s) => {
                                        const Icon = SOCIAL_ICONS[s.id];
                                        return (
                                            <a
                                                key={s.id}
                                                href={s.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={s.label}
                                                data-testid={`menu-social-${s.id}`}
                                                className="w-11 h-11 rounded-xl bg-obsidian border border-white/15 flex items-center justify-center text-slate-400 hover:text-purp-neon hover:border-purp/50 transition-colors"
                                            >
                                                <Icon className="w-5 h-5" />
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                        </motion.aside>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
}
